import {
  ChoiceGroup,
  CommandButton,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  IChoiceGroupOption,
  IDropdownOption,
  IPanelProps,
  IRenderFunction,
  Panel,
  PanelType,
  Stack,
  Text,
  TextField
} from '@fluentui/react';
import { Action, ActionType } from 'components/rulesdoc/rulesdoc.model';
import { TenantTitles } from 'components/rulesdoc/RulesDocComponent';
import { SalusPrimaryButton } from 'components/shared/SalusPrimaryButton';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
  panelActionDetailsStyles,
  stackActionPanelNavigationContainerStyles,
  textActionPanelNavigationHeaderStyles,
  textFieldActionNameStyles,
  dropdownSubjectTypeStyles,
  dropdownTitleIdStyles,
  stackActionPanelCloseButtonContainerStyles,
  textFieldParameterValueStyles,
  choiceGroupParameterValueStyles,
  stackParameterRowContainerStyles,
  dropdownParameterTypeContainerStyles,
  textFieldParameterNameValueStyles,
  stackAddParameterButtonContainerStyles
} from './ActionDetailsPanelStyles';

const MAX_ALLOWED_NUMBER_OF_PARAMETERS = 9;

const dropdownSubjectTypeOptions: IDropdownOption[] = [
  { key: 'Subject', text: 'Subject' },
  { key: 'SubjectOwner', text: 'Subject Owner' },
  { key: 'Source', text: 'Source' },
  { key: 'Any', text: 'Any' },
  { key: 'None', text: 'None' },
  { key: 'Manual', text: 'Manual' }
];

const choiceGroupBooleanOptions: IChoiceGroupOption[] = [
  { key: 'true', text: 'true', styles: { root: { marginTop: '0px' } } },
  { key: 'false', text: 'false', styles: { root: { marginTop: '0px', marginLeft: '20px' } } }
];

interface FormattedParameter {
  name: string;
  type: IDropdownOption;
  value: string | boolean | undefined;
}

const createActionDialogContentProps = {
  type: DialogType.close,
  title: 'Create action',
  closeButtonAriaLable: 'close',
  subText: 'Once an action is created it can not be edited.'
};

interface ActionDetailsPanelProps {
  action: Action | undefined;
  tenantId: string | undefined;
  isPanelOpen: boolean;
  closePanel: () => void;
  isReadOnly: boolean;
  enableReadOnly: () => void;
  updateActionsList: (action: Action) => void;
  tenantTitles: TenantTitles;
}

export const ActionDetailsPanel: FunctionComponent<ActionDetailsPanelProps> = props => {
  const [actionName, setActionName] = useState<string>();
  const [subjectType, setSubjectType] = useState<IDropdownOption>();
  const [titleId, setTitleId] = useState<IDropdownOption>();
  const [parameters, setParameters] = useState<FormattedParameter[]>([]);

  const [isCreateActionDialogHidden, setIsCreateActionDialogHidden] = useState(true);
  const [dropdownTitleIdOptions, setDropdownTitleIdOptions] = useState<IDropdownOption[]>([]);

  useEffect(() => {
    setupActionDetailsPanelData(props.action);
  }, [props.action]);

  useEffect(() => {
    createDropdownTitleIdOptions();
  }, [props.tenantTitles]);

  const setupActionDetailsPanelData = (actionItem: Action | undefined) => {
    if (actionItem) {
      setActionName(actionItem.action);
      setSubjectType({ key: actionItem.actionType, text: '' });
      setTitleId({ key: actionItem.titleId, text: '' });
      setParameters(formatParameters(actionItem.parameters));
    } else {
      setActionName(undefined);
      setSubjectType(undefined);
      setTitleId(undefined);
      setParameters([{ name: '', type: { key: '', text: '' }, value: undefined }]);
    }
  };

  const createDropdownTitleIdOptions = () => {
    const dropdownTitleIdOptions: IDropdownOption[] = Object.keys(props.tenantTitles).map(title => {
      return { key: title, text: title };
    });
    setDropdownTitleIdOptions(dropdownTitleIdOptions);
  };

  const formatParameters = (parameters: Record<string, string | boolean>): FormattedParameter[] => {
    const formattedParameters: FormattedParameter[] = [];
    Object.keys(parameters).forEach(parameter => {
      const formattedParameter: FormattedParameter = {
        name: parameter,
        type: { key: '', text: '' },
        value: parameters[parameter]
      };
      if (typeof formattedParameter.value === 'string') {
        formattedParameter.type.key = 'string';
        // check if @ character exists at the beginning of the string, if yes, omit
        if (formattedParameter.value.charAt(0) === '@') {
          formattedParameter.value = formattedParameter.value.substring(1);
        }
      } else if (typeof formattedParameter.value === 'boolean') {
        formattedParameter.type.key = 'boolean';
      }
      formattedParameters.push(formattedParameter);
    });
    return formattedParameters;
  };

  const unformatParameters = (formattedParameters: FormattedParameter[]): Record<string, string | boolean> => {
    const unformattedParameters: Record<string, string | boolean> = {};
    formattedParameters.forEach(formattedParameter => {
      if (formattedParameter.value !== undefined) {
        if (formattedParameter.type.key === 'boolean' && typeof formattedParameter.value === 'boolean') {
          unformattedParameters[formattedParameter.name] = formattedParameter.value;
        } else if (formattedParameter.type.key === 'string' && typeof formattedParameter.value === 'string') {
          if (formattedParameter.value.charAt(0) !== '@') {
            // append @ at the beginning of the string
            unformattedParameters[formattedParameter.name] = '@' + formattedParameter.value;
          } else {
            unformattedParameters[formattedParameter.name] = formattedParameter.value;
          }
        }
      }
    });
    return unformattedParameters;
  };

  const onRenderActionPanelNavigationContent: IRenderFunction<IPanelProps> = useCallback(
    (props, defaultRender) => (
      <Stack horizontal horizontalAlign="space-between" styles={stackActionPanelNavigationContainerStyles}>
        <Text styles={textActionPanelNavigationHeaderStyles}>{'Action details'}</Text>
        <Stack horizontal styles={stackActionPanelCloseButtonContainerStyles}>
          {defaultRender && defaultRender(props)}
        </Stack>
      </Stack>
    ),
    []
  );

  const isCreateActionButtonDisabled = () => {
    let isDisabled = true;
    if (actionName !== undefined && actionName.trim() !== '' && subjectType && titleId) {
      let parameterListFullyPopulated = true;
      // go through parameter list. If all fields of each parameter is filled, set isDisabled to false
      for (let i = 0; i < parameters.length; i++) {
        const currentParameter = parameters[i];
        if (
          currentParameter.name.trim() === '' ||
          currentParameter.type.key === '' ||
          currentParameter.value === undefined ||
          (typeof currentParameter.value === 'string' && currentParameter.value.trim() === '')
        ) {
          parameterListFullyPopulated = false;
          break;
        }
      }
      if (parameterListFullyPopulated) {
        isDisabled = false;
      }
    }
    return isDisabled;
  };

  const saveFieldsToAction = () => {
    if (actionName !== undefined && subjectType && titleId && props.tenantId !== undefined) {
      if (typeof subjectType.key === 'string' && typeof titleId.key === 'string') {
        const actionToAdd: Action = {
          action: actionName,
          actionType: ActionType[subjectType.key as keyof typeof ActionType],
          parameters: unformatParameters(parameters),
          tenantId: props.tenantId,
          titleId: titleId.key
        };
        props.updateActionsList(actionToAdd);
      }
    }
  };

  const onRenderActionPanelFooterContent = () => {
    if (props.isReadOnly) {
      return (
        <SalusPrimaryButton
          text="Done"
          onClick={() => {
            props.closePanel();
            resetInputFields();
          }}
        />
      );
    } else {
      return (
        <>
          <SalusPrimaryButton
            text="Create action"
            onClick={() => openCreateActionDialog()}
            disabled={isCreateActionButtonDisabled()}
            styles={{ root: { marginRight: '8px' } }}
          />
          <DefaultButton
            text="Cancel"
            onClick={() => {
              props.closePanel();
              resetInputFields();
            }}
          />
        </>
      );
    }
  };

  const onChangeActionNameTextField = useCallback(
    (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
      if (newValue !== undefined) {
        setActionName(newValue);
      }
    },
    []
  );

  const onChangeParameterNameTextField = (index: number, newParameterName?: string) => {
    if (newParameterName !== undefined) {
      if (parameters[index]) {
        const formattedParameter: FormattedParameter = { ...parameters[index] };
        formattedParameter.name = newParameterName;

        const parametersCopy = [...parameters];
        parametersCopy[index] = formattedParameter;
        setParameters(parametersCopy);
      }
    }
  };

  const onChangeParameterTypeDropdown = (index: number, parameterTypeOption?: IDropdownOption) => {
    if (parameterTypeOption) {
      if (parameters[index]) {
        const formattedParameter: FormattedParameter = { ...parameters[index] };
        formattedParameter.type = parameterTypeOption;
        // reset the parameter value after parameter type dropdown onChange
        formattedParameter.value = undefined;

        const parametersCopy = [...parameters];
        parametersCopy[index] = formattedParameter;
        setParameters(parametersCopy);
      }
    }
  };

  const onChangeParameterValueTextField = (index: number, newParameterValue?: string) => {
    if (newParameterValue !== undefined) {
      if (parameters[index]) {
        const formattedParameter: FormattedParameter = { ...parameters[index] };
        formattedParameter.value = newParameterValue;

        const parametersCopy = [...parameters];
        parametersCopy[index] = formattedParameter;
        setParameters(parametersCopy);
      }
    }
  };

  const onChangeParameterValueDropdown = (index: number, option?: IChoiceGroupOption) => {
    if (option) {
      if (parameters[index]) {
        const formattedParameter: FormattedParameter = { ...parameters[index] };
        formattedParameter.value = option.key === 'true' ? true : false;

        const parametersCopy = [...parameters];
        parametersCopy[index] = formattedParameter;
        setParameters(parametersCopy);
      }
    }
  };

  const onChangeSubjectTypeDropdown = (
    event: React.FormEvent<HTMLDivElement>,
    actionTypeDropdownOption?: IDropdownOption
  ): void => {
    if (actionTypeDropdownOption) {
      setSubjectType(actionTypeDropdownOption);
    }
  };

  const onChangeTitleIdDropdown = (
    event: React.FormEvent<HTMLDivElement>,
    titleIdDropdownOption?: IDropdownOption
  ): void => {
    if (titleIdDropdownOption) {
      setTitleId(titleIdDropdownOption);
    }
  };

  const resetInputFields = () => {
    setActionName(undefined);
    setSubjectType(undefined);
    setTitleId(undefined);
    setParameters([{ name: '', type: { key: '', text: '' }, value: undefined }]);
  };

  const renderParameterValueView = (index: number, paramValue: string | boolean | undefined) => {
    if (parameters[index]) {
      const formattedParameter: FormattedParameter = { ...parameters[index] };
      if (formattedParameter.type.key === 'string' && typeof paramValue !== 'boolean') {
        return (
          <TextField
            placeholder={'Enter parameter value'}
            prefix="@"
            value={paramValue || ''}
            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newParameterValue?: string) => {
              onChangeParameterValueTextField(index, newParameterValue);
            }}
            disabled={props.isReadOnly}
            styles={textFieldParameterValueStyles}
          />
        );
      }
      if (formattedParameter.type.key === 'boolean' && typeof paramValue !== 'string') {
        let optionStringRepresentation: string | undefined = undefined;
        if (paramValue !== undefined) {
          optionStringRepresentation = paramValue ? 'true' : 'false';
        }
        return (
          <ChoiceGroup
            options={choiceGroupBooleanOptions}
            selectedKey={optionStringRepresentation}
            onChange={(event?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
              onChangeParameterValueDropdown(index, option);
            }}
            disabled={props.isReadOnly}
            styles={choiceGroupParameterValueStyles}
          />
        );
      }
    }
  };

  const createParametersView = () => {
    return (
      <Stack>
        {parameters.map((parameter, index) => {
          return (
            <Stack horizontal verticalAlign="end" key={index} styles={stackParameterRowContainerStyles}>
              <TextField
                label={index === 0 ? 'Parameters' : undefined}
                value={parameter.name}
                placeholder={'Enter parameter name'}
                onChange={(
                  event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                  newParameterName?: string
                ) => {
                  onChangeParameterNameTextField(index, newParameterName);
                }}
                disabled={props.isReadOnly}
                styles={textFieldParameterNameValueStyles}
              />
              <Dropdown
                options={[
                  { key: 'string', text: 'string' },
                  { key: 'boolean', text: 'boolean' }
                ]}
                selectedKey={parameter.type ? parameter.type.key : undefined}
                placeholder="Parameter type"
                ariaLabel="Parameter type dropdown"
                onChange={(event: React.FormEvent<HTMLDivElement>, parameterTypeOption?: IDropdownOption): void => {
                  onChangeParameterTypeDropdown(index, parameterTypeOption);
                }}
                disabled={props.isReadOnly}
                styles={dropdownParameterTypeContainerStyles}
              />
              {renderParameterValueView(index, parameter.value)}
            </Stack>
          );
        })}
      </Stack>
    );
  };

  const addParameter = () => {
    const newParameterToAdd: FormattedParameter = { name: '', type: { key: '', text: '' }, value: undefined };
    const parametersCopy = [...parameters];
    parametersCopy.push(newParameterToAdd);
    setParameters(parametersCopy);
  };

  const isAddParameterButtonDisabled = () => {
    let isDisabled = true;
    if (parameters.length > 0) {
      const lastParameterInList: FormattedParameter = { ...parameters[parameters.length - 1] };

      if (
        lastParameterInList.name.trim() !== '' &&
        lastParameterInList.type.key !== '' &&
        lastParameterInList.value !== undefined
      ) {
        if (typeof lastParameterInList.value === 'string' && lastParameterInList.value.trim() !== '') {
          isDisabled = false;
        } else if (
          typeof lastParameterInList.value === 'boolean' &&
          (lastParameterInList.value === true || lastParameterInList.value === false)
        ) {
          isDisabled = false;
        }
      }
    }
    return isDisabled;
  };

  const closeCreateActionDialog = () => {
    setIsCreateActionDialogHidden(true);
  };

  const openCreateActionDialog = () => {
    setIsCreateActionDialogHidden(false);
  };

  return (
    <Panel
      type={PanelType.medium}
      isOpen={props.isPanelOpen}
      onDismiss={() => {
        props.closePanel();
        resetInputFields();
      }}
      closeButtonAriaLabel="Close action details panel"
      onRenderNavigationContent={onRenderActionPanelNavigationContent}
      onRenderFooterContent={onRenderActionPanelFooterContent}
      isFooterAtBottom={true}
      styles={panelActionDetailsStyles}
    >
      <Dialog hidden={isCreateActionDialogHidden} dialogContentProps={createActionDialogContentProps}>
        <DialogFooter>
          <SalusPrimaryButton
            text="Create action"
            onClick={() => {
              closeCreateActionDialog();
              saveFieldsToAction();
              props.enableReadOnly();
            }}
          />
          <DefaultButton
            text="Cancel"
            onClick={() => {
              closeCreateActionDialog();
            }}
          />
        </DialogFooter>
      </Dialog>
      <TextField
        label="Action name"
        placeholder="Add a name for your action"
        value={actionName || ''}
        onChange={onChangeActionNameTextField}
        disabled={props.isReadOnly}
        styles={textFieldActionNameStyles}
      />
      <Dropdown
        label="Action type"
        placeholder="Select from the action types"
        options={dropdownSubjectTypeOptions}
        defaultSelectedKey={subjectType ? subjectType.key : undefined}
        onChange={onChangeSubjectTypeDropdown}
        disabled={props.isReadOnly}
        styles={dropdownSubjectTypeStyles}
      />
      <Dropdown
        label="TitleIDs"
        placeholder="Select one or more"
        options={dropdownTitleIdOptions}
        defaultSelectedKey={titleId ? titleId.key : undefined}
        onChange={onChangeTitleIdDropdown}
        disabled={props.isReadOnly}
        styles={dropdownTitleIdStyles}
      />
      {createParametersView()}
      {!props.isReadOnly && parameters.length < MAX_ALLOWED_NUMBER_OF_PARAMETERS && (
        <Stack horizontal styles={stackAddParameterButtonContainerStyles}>
          <CommandButton
            iconProps={{ iconName: 'Add' }}
            text="Add parameters"
            onClick={() => addParameter()}
            disabled={isAddParameterButtonDisabled()}
          />
        </Stack>
      )}
    </Panel>
  );
};
